import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  CustomButtonGroup,
  DropdownSearch,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/common';
import { AppRoutes } from '@/constants';
import { navigate } from '@/providers';
import {
  handleServerValidationErrors,
  mapFaceIdProfileResponseToDropdownSearchResponse,
  showAlertSuccess,
} from '@/utils';
import { FaceIdProfileService } from '@/services';

import { IRoleAssignmentUpdate } from '@/features/role-assignments/interfaces';
import {
  ROLE_ASSIGNMENT_FORM_FIELDS,
  ROLE_ASSIGNMENT_REMARKS_MAX_LENGTH,
  ROLE_ASSIGNMENT_UPDATE_SCHEMA,
} from '@/features/role-assignments/constants';
import { RoleStatus } from '@/features/role-assignments/enums';
import { getRoleStatusLabel } from '@/features/role-assignments/utils';
import {
  useRoleAssignmentMutation,
  useRoleAssignmentQuery,
} from '@/features/role-assignments/hooks';

const RoleAssignmentCreation = () => {
  const {
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<IRoleAssignmentUpdate>({
    resolver: yupResolver(ROLE_ASSIGNMENT_UPDATE_SCHEMA),
  });
  const profileId = watch(ROLE_ASSIGNMENT_FORM_FIELDS.profileId);
  const { faceIdProfileQuery, rolesQuery } = useRoleAssignmentQuery(profileId);
  const { creationMutation } = useRoleAssignmentMutation();

  const onSubmit = async (data: IRoleAssignmentUpdate) => {
    await creationMutation.mutateAsync(data, {
      onSuccess: () => {
        navigate(AppRoutes.ROLE_ASSIGNMENTS);
        showAlertSuccess('User role assigned successfully!');
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
  };
  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Header with Title */}
      <h1 className="text-h1 font-bold">Assign Role</h1>

      <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
        <div className="text-sm flex flex-col gap-y-base-plus p-m">
          <Controller
            name={ROLE_ASSIGNMENT_FORM_FIELDS.profileId}
            control={control}
            render={({ field }) => (
              <DropdownSearch
                label="Face Profile ID"
                placeholder="Face Profile ID"
                onSelect={field.onChange}
                searchCallback={async (searchValue) =>
                  mapFaceIdProfileResponseToDropdownSearchResponse(
                    await FaceIdProfileService.GetFaceIdProfiles(searchValue),
                  )
                }
                required
                error={errors[ROLE_ASSIGNMENT_FORM_FIELDS.profileId]?.message}
              />
            )}
          />
          <Input
            label="Full Name"
            placeholder="Full name"
            value={faceIdProfileQuery.data?.fullName ?? ''}
            required
            disabled
          />
          <Input
            label="Employee ID"
            placeholder="Employee ID"
            value={faceIdProfileQuery.data?.employeeId ?? ''}
            required
            disabled
          />
          <Input
            label="Email"
            placeholder="Email"
            value={faceIdProfileQuery.data?.email ?? ''}
            required
            disabled
          />
          <Controller
            name={ROLE_ASSIGNMENT_FORM_FIELDS.roleId}
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                label="Role"
                required
                error={errors[ROLE_ASSIGNMENT_FORM_FIELDS.roleId]?.message}
              >
                <SelectTrigger allowClear className="w-full">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {rolesQuery.data?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <div className="flex flex-col gap-1.5 border-neutral-n4 w-full">
            <Label className="!gap-0 font-semibold">
              Status<span className="ml-1 text-semantic-error-base">*</span>
            </Label>
            <Controller
              name={ROLE_ASSIGNMENT_FORM_FIELDS.status}
              control={control}
              defaultValue={RoleStatus.Active}
              render={({ field }) => (
                <CustomButtonGroup
                  buttonNames={[
                    { name: getRoleStatusLabel(RoleStatus.Active), value: RoleStatus.Active },
                    { name: getRoleStatusLabel(RoleStatus.Inactive), value: RoleStatus.Inactive },
                  ]}
                  onSelect={field.onChange}
                  selectedIndex={field.value}
                />
              )}
            />
          </div>
          <Controller
            name={ROLE_ASSIGNMENT_FORM_FIELDS.remarks}
            control={control}
            render={({ field }) => (
              <Textarea
                label="Remark"
                placeholder="Enter remarks"
                className="[&_textarea]:min-h-28"
                onChange={field.onChange}
                maxLength={ROLE_ASSIGNMENT_REMARKS_MAX_LENGTH}
                error={errors[ROLE_ASSIGNMENT_FORM_FIELDS.remarks]?.message}
              />
            )}
          />
        </div>
      </div>
      {/* back and submit button */}
      <div className="flex justify-end gap-s-plus">
        <Button
          className="w-[104px]"
          size="lg"
          variant="cancel"
          borderRadius="soft"
          onClick={() => navigate(AppRoutes.ROLE_ASSIGNMENTS)}
        >
          Back
        </Button>
        <Button
          className="w-[104px]"
          size="lg"
          variant="primary"
          borderRadius="soft"
          onClick={handleSubmit(onSubmit)}
          disabled={creationMutation.isPending}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default RoleAssignmentCreation;
